/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerQuery, useQuery, registerMutation, useMutation } from 'academy-common-lib/utils/DataFetchingClient';
import { getQueryFieldName, queryDataFromCache, updateCache } from '../../utils/DataFetchingClientCache';
import http from 'hub-http/clients/apiClient';
import { GET_PLAYLIST, updatePlaylistFromCache, useUpdatePlaylist } from '../savedItems/savedItemsQueries';
import { useCallback } from 'react';
import { BASE_SERVICE_URI } from '../../constants/ApiConstants';
const PLAYLISTS_API = `${BASE_SERVICE_URI}/playlists/v1`;
export const MY_SAVED_PLAYLISTS_URL = `${PLAYLISTS_API}/user/saved`;
export const MY_PLAYLISTS_URL = `${PLAYLISTS_API}/search`;
const addPlaylistsToCache = playlists => {
  playlists.forEach(playlist => {
    const variables = {
      playlistId: playlist.id
    };
    if (!queryDataFromCache(GET_PLAYLIST, variables)) {
      updateCache(GET_PLAYLIST, () => playlist, variables);
    }
  });
};
export const FETCH_MY_PLAYLISTS = registerQuery({
  fieldName: 'myPlaylists',
  args: ['interactionTypes', 'offset', 'limit', 'query', 'sortProperty', 'sortOrder'],
  fetcher({
    interactionTypes,
    offset,
    limit,
    query,
    sortProperty,
    sortOrder
  }) {
    return http.get(MY_PLAYLISTS_URL, {
      query: {
        interactionTypes,
        offset,
        limit,
        query,
        sortProperty,
        sortOrder
      }
    }).then(result => {
      if (!result) return result;
      addPlaylistsToCache(result.results);
      return result;
    });
  }
});
export const useMyPlaylists = ({
  interactionTypes = ['CREATED', 'ASSIGNED', 'SAVED'],
  limit,
  page,
  query,
  sortProperty,
  sortOrder,
  skip = false
} = {
  interactionTypes: ['CREATED', 'ASSIGNED', 'SAVED']
}) => {
  const {
    data: playlistsResponse,
    loading: playlistsLoading,
    error: playlistsError
  } = useQuery(FETCH_MY_PLAYLISTS, {
    skip,
    variables: {
      interactionTypes,
      offset: limit && page ? limit * (page - 1) : undefined,
      limit: limit || undefined,
      query,
      sortProperty,
      sortOrder
    }
  });
  return {
    totalPlaylists: playlistsResponse ? playlistsResponse.total : 0,
    offset: playlistsResponse ? playlistsResponse.offset : 0,
    hasMore: playlistsResponse ? playlistsResponse.hasMore : false,
    playlists: playlistsResponse ? playlistsResponse.results : null,
    playlistsLoading,
    playlistsError
  };
};
export const refetchMyPlaylists = () => {
  // Unfortunately refetch has a bug and doesn't keep track of query usages
  // in other components and doesn't refetch them. This was the proposed
  // solution to solve the refetching problem.
  dataFetchingClient.refetchQueries({
    updateCache(cache) {
      cache.evict({
        fieldName: getQueryFieldName(FETCH_MY_PLAYLISTS)
      });
    }
  });
};
export const useSetHiddenForOwner = (playlistId, hiddenForOwner) => {
  const onCompleted = () => {
    refetchMyPlaylists();
  };
  const [mutate, {
    updatePlaylistMutationLoading: archievePlaylistMutationLoading,
    updatePlaylistMutationCalled: archievePlaylistMutationCalled,
    updatePlaylistMutationError: archievePlaylistMutationError
  }] = useUpdatePlaylist(playlistId, {
    onCompleted
  });
  const setHiddenForOwner = useCallback(() => {
    return mutate({
      hiddenForOwner
    });
  }, [hiddenForOwner, mutate]);
  return [setHiddenForOwner, {
    archievePlaylistMutationLoading,
    archievePlaylistMutationCalled,
    archievePlaylistMutationError
  }];
};
export const SAVE_PLAYLIST = registerMutation({
  fieldName: 'savePlaylist',
  args: ['playlistId'],
  fetcher({
    playlistId
  }) {
    return http.post(`${MY_SAVED_PLAYLISTS_URL}`, {
      data: {
        playlistId
      }
    });
  }
});
export const useSavePlaylist = playlistId => {
  const [mutate, {
    loading: savePlaylistMutationLoading,
    called: savePlaylistMutationCalled,
    error: savePlaylistMutationError
  }] = useMutation(SAVE_PLAYLIST, {
    variables: {
      playlistId
    },
    update: () => {
      refetchMyPlaylists();
      updatePlaylistFromCache(playlistId, {
        savedByUser: true
      });
    }
  });
  return [mutate, {
    savePlaylistMutationLoading,
    savePlaylistMutationCalled,
    savePlaylistMutationError
  }];
};
export const UNSAVE_PLAYLIST = registerMutation({
  fieldName: 'unsavePlaylist',
  args: ['playlistId'],
  fetcher({
    playlistId
  }) {
    return http.delete(`${MY_SAVED_PLAYLISTS_URL}/${playlistId}`);
  }
});
export const useUnsavePlaylist = playlistId => {
  const [mutate, {
    loading: unsavePlaylistMutationLoading,
    called: unsavePlaylistMutationCalled,
    error: unsavePlaylistMutationError
  }] = useMutation(UNSAVE_PLAYLIST, {
    variables: {
      playlistId
    },
    update: () => {
      refetchMyPlaylists();
      updatePlaylistFromCache(playlistId, {
        savedByUser: false
      });
    }
  });
  return [mutate, {
    unsavePlaylistMutationLoading,
    unsavePlaylistMutationCalled,
    unsavePlaylistMutationError
  }];
};